import React from "react"

import { Loader2 } from "lucide-react"

import { cn } from "@/shared/lib/utils"

export interface LoadingOverlayProps {
  /**
   * Whether the overlay is active
   */
  isLoading: boolean

  /**
   * Optional children to render inside the overlay
   */
  children?: React.ReactNode

  /**
   * Custom spinner component (defaults to Loader2)
   */
  spinner?: React.ReactNode

  /**
   * Custom spinner size
   */
  spinnerSize?: "sm" | "md" | "lg"

  /**
   * Base style for the spinner wrapper
   */
  spinnerWrapperClassName?: string

  /**
   * Base style for the overlay
   */
  overlayClassName?: string

  /**
   * Background color/opacity for the overlay
   */
  backdropClassName?: string

  /**
   * Color of the spinner
   */
  spinnerColor?: string
}

/**
 * A reusable loading overlay component that can be used to indicate loading state
 * for any container element.
 */
export const LoadingOverlay: React.FC<LoadingOverlayProps> = ({
  isLoading,
  children,
  spinner,
  spinnerSize = "md",
  spinnerWrapperClassName,
  overlayClassName,
  backdropClassName,
  spinnerColor = "text-yellow-500"
}) => {
  // Early return if not loading and no children
  if (!isLoading && !children) return null

  // Map spinner sizes to actual size values
  const spinnerSizeMap = {
    sm: "h-4 w-4",
    md: "h-5 w-5",
    lg: "h-6 w-6"
  }

  return (
    <div className={cn("relative", overlayClassName)}>
      {isLoading && (
        <div
          className={cn(
            "absolute inset-0 z-10 flex items-center justify-center backdrop-blur-[1px]",
            backdropClassName || "bg-gray-900/20"
          )}>
          <div
            className={cn(
              "rounded-full bg-gray-900/70 shadow-lg",
              spinnerSize === "sm" ? "p-1.5" : spinnerSize === "md" ? "p-2" : "p-3",
              spinnerWrapperClassName
            )}>
            {spinner || <Loader2 className={cn("animate-spin", spinnerSizeMap[spinnerSize], spinnerColor)} />}
          </div>
        </div>
      )}
      {children}
    </div>
  )
}

export default LoadingOverlay
